<main class="mb-page">

  <h1 i18n="@@rechnerPage.h1">Was kostet ein Verkaufsautomat – und ab wann rechnet er sich?</h1>

  <p class="mb-lead" i18n="@@rechnerPage.lead">
    Die ehrliche Antwort lautet: Der Kaufpreis ist selten das Problem. Entscheidend ist, was danach passiert –
    wie viel Umsatz der Automat macht, was der Betrieb wirklich kostet und wie viele Monate vergehen,
    bis das Ding sich selbst bezahlt hat. Genau das rechnen Sie hier durch, mit echten Zahlen aus
    <strong>den ersten 450 marktbox-Automaten</strong> an Standorten in Deutschland.
  </p>

  <div class="mb-quote">
    <p i18n="@@rechnerPage.quote.unit">
      <strong>Was hier „ein Automat" heißt:</strong> eine marktbox mit 39 Fächern – die Einheit, die Sie kaufen
      oder mieten. Steht der Standort und reicht der Platz nicht mehr, stellen Sie einfach einen zweiten daneben.
      Alle Zahlen auf dieser Seite beziehen sich auf <em>einen</em> Automaten.
      <a href="#mehrere" (click)="scrollToAnchor($event, 'mehrere')">Was ein zweiter kostet und bringt</a>
    </p>
  </div>

  <div class="mb-trust">
    <div><b>2.076 €</b><span i18n="@@rechnerPage.trust.revenue">Ø Umsatz pro Automat und Monat</span></div>
    <div><b>56 €</b><span i18n="@@rechnerPage.trust.costs">laufende Kosten pro Monat</span></div>
    <div><b>7,5</b><span i18n="@@rechnerPage.trust.breakeven">Monate bis zur Amortisation</span></div>
  </div>
  <p class="mb-hint mb-center" i18n="@@rechnerPage.trust.hint">
    Mittelwerte aus den ersten 450 marktbox-Automaten an Standorten in Deutschland. Alle Beträge in Euro, netto.
  </p>

  <!-- ==================== RECHNER ==================== -->
  <h2 id="rechner" i18n="@@rechnerPage.calc.h2">Rechnen Sie Ihren eigenen Fall durch</h2>
  <p i18n="@@rechnerPage.calc.intro">
    Schieben Sie die Regler auf Ihre Situation. Sie sehen sofort, was pro Monat übrig bleibt und
    nach wie vielen Monaten der Automat bezahlt ist. Wenn Sie Ihre Zahlen noch nicht kennen:
    Der Button oben im Rechner setzt alles auf den Durchschnitt unserer Kunden.
  </p>
  <p class="mb-hint" i18n="@@rechnerPage.calc.hint">
    Der Rechner rechnet <strong>einen Automaten</strong>. Wer zwei nebeneinander stellt, rechnet nicht einfach
    mal zwei – <a href="#mehrere" (click)="scrollToAnchor($event, 'mehrere')">der zweite ist günstiger im Betrieb</a>.
  </p>

  <app-amortisation-calculator />

  <!-- ==================== CONTENT ==================== -->
  <h2 i18n="@@rechnerPage.buy.h2">Was kostet ein Verkaufsautomat in der Anschaffung?</h2>
  <p i18n="@@rechnerPage.buy.p1">
    Eine marktbox kostet <strong>6.800 € netto im Kauf</strong> oder <strong>300 € netto im Monat in der Miete</strong>
    bei 12 Monaten Laufzeit. Dafür bekommen Sie 39 Fächer, bargeldloses Bezahlen, Mobilfunkanbindung und die WebApp,
    über die Sie Bestände, Verkäufe und Preise steuern.
  </p>
  <p i18n="@@rechnerPage.buy.p2">
    Zum Vergleich: Gebrauchte Snack- oder Warenautomaten gibt es ab wenigen hundert Euro – dafür meist ohne
    Kartenzahlung, ohne Fernüberwachung und mit Wartungsaufwand, den Sie selbst tragen. Die Rechnung dazu steht im
    Artikel <a [routerLink]="['/', links.blog, 'gebrauchte-verkaufsautomaten']">Gebrauchte Verkaufsautomaten</a>.
  </p>

  <h2 i18n="@@rechnerPage.setup.h2">Was kostet es, einen Verkaufsautomaten aufzustellen?</h2>
  <p i18n="@@rechnerPage.setup.p1">
    Der Automat ist das eine, das Aufstellen das andere – und hier ist die gute Nachricht, dass es fast nichts kostet.
    Eine marktbox braucht zum Aufstellen nur zwei Dinge: einen <strong>festen, ebenen Untergrund</strong> und eine
    <strong>normale Steckdose</strong>. Kein Fundament, kein Wasseranschluss, kein Bagger.
  </p>
  <p i18n="@@rechnerPage.setup.p2">
    Wer einen Automaten auf dem eigenen Grundstück aufstellt – am Hof, vor dem Laden, an der Hofeinfahrt – hat damit
    in der Regel keine weiteren Kosten. Anders sieht es aus, wenn Sie eine fremde Fläche nutzen: Dann kommt eine
    Stellplatzmiete dazu, die Sie in der Rechnung oben schlicht vom Überschuss abziehen. Und je nach Bundesland und
    Standort kann eine Genehmigung nötig werden, vor allem im öffentlichen Raum. Was dabei zu beachten ist, steht im
    <a [routerLink]="['/', links.guide, guideLocationPath]">Standortguide</a>.
  </p>

  <h2 i18n="@@rechnerPage.running.h2">Die laufenden Kosten – der Teil, den fast alle überschätzen</h2>
  <p i18n="@@rechnerPage.running.p1">
    Hier liegt die eigentliche Überraschung. Ein Verkaufsautomat ist im Betrieb bemerkenswert billig,
    weil er weder Personal noch Miete noch Öffnungszeiten braucht:
  </p>

  <table class="mb-table">
    <thead>
      <tr><th i18n="@@rechnerPage.running.th1">Kostenpunkt</th><th i18n="@@rechnerPage.running.th2">Pro Monat</th></tr>
    </thead>
    <tbody>
      <tr><td i18n="@@rechnerPage.running.row.power">Strom (60 Watt im Dauerbetrieb, 0,45 €/kWh)</td><td>19,71 €</td></tr>
      <tr><td i18n="@@rechnerPage.running.row.insurance">Versicherung (200 € im Jahr)</td><td>16,67 €</td></tr>
      <tr><td i18n="@@rechnerPage.running.row.mobile">Mobilfunk und Anbindung</td><td>20,00 €</td></tr>
      <tr><td i18n="@@rechnerPage.running.row.fees">Zahlungsgebühren (10 ct pro Transaktion, keine Prozente)</td><td i18n="@@rechnerPage.running.row.fees.value">ca. 36 €*</td></tr>
    </tbody>
    <tfoot>
      <tr><td i18n="@@rechnerPage.running.row.total">Summe im Durchschnittsfall</td><td i18n="@@rechnerPage.running.row.total.value">ca. 92 €</td></tr>
    </tfoot>
  </table>
  <p class="mb-hint" i18n="@@rechnerPage.running.footnote">* Bei 2.076 € Umsatz und 5,73 € Durchschnittsbon sind das rund 362 Einkäufe im Monat.</p>

  <div class="mb-quote">
    <p i18n="@@rechnerPage.running.quote">
      <strong>Rechnen Sie kurz mit:</strong> Bei einem üblichen Zahlungsanbieter mit 1,5 % plus 25 ct pro Transaktion
      wären dieselben 362 Einkäufe rund <strong>122 € statt 36 €</strong>. Über zwölf Monate ein Unterschied von
      über 1.000 € – bei identischem Umsatz. Deshalb ist die Gebührenstruktur beim Automatenkauf keine Fußnote.
    </p>
  </div>

  <p i18n="@@rechnerPage.running.p2">
    Wartung und Reinigung tauchen in der Tabelle bewusst nicht als Fixposten auf. Sie fallen anlassbezogen an,
    und bei den meisten unserer Kunden bedeutet das: gelegentlich durchwischen, wenn ohnehin nachgefüllt wird.
    Mehr dazu im Artikel <a [routerLink]="['/', links.blog, 'laufende-kosten-von-automaten']">Laufende Kosten von Automaten</a>.
  </p>

  <h2 i18n="@@rechnerPage.revenue.h2">Wie viel Umsatz macht ein Verkaufsautomat wirklich?</h2>
  <p i18n="@@rechnerPage.revenue.p1">
    Über die ersten 450 marktbox-Automaten liegt der Durchschnitt bei <strong>2.076 € Umsatz pro Monat</strong>.
    Diese Zahl allein ist allerdings wenig wert, weil die Spanne groß ist:
  </p>

  <table class="mb-table">
    <thead>
      <tr><th i18n="@@rechnerPage.revenue.th1">Standortqualität</th><th i18n="@@rechnerPage.revenue.th2">Umsatz pro Monat</th></tr>
    </thead>
    <tbody>
      <tr><td i18n="@@rechnerPage.revenue.row.weak">Schwacher Standort (wenig Laufkundschaft, kaum sichtbar)</td><td i18n="@@rechnerPage.revenue.row.weak.value">ca. 600 €</td></tr>
      <tr><td i18n="@@rechnerPage.revenue.row.avg">Durchschnitt über alle Automaten</td><td>2.076 €</td></tr>
      <tr><td i18n="@@rechnerPage.revenue.row.strong">Starker Standort (Durchgangsverkehr, gute Sichtbarkeit)</td><td i18n="@@rechnerPage.revenue.row.strong.value">bis 4.800 €</td></tr>
    </tbody>
  </table>

  <p i18n="@@rechnerPage.revenue.p2">
    Der Unterschied zwischen 600 € und 4.800 € ist nicht der Automat. Es ist der Standort. Wer daran zweifelt, ob der
    eigene Platz taugt, findet die Kriterien im <a [routerLink]="['/', links.guide, guideLocationPath]">Standortguide</a> – das ist die
    wichtigste Entscheidung im ganzen Projekt, wichtiger als Kauf oder Miete.
  </p>

  <h2 i18n="@@rechnerPage.example.h2">Wie rentabel sind Automaten? Ein Beispiel</h2>
  <p i18n="@@rechnerPage.example.p1">
    Nehmen Sie den Durchschnittsfall: 2.076 € Umsatz, eigene Erzeugnisse mit 48 % Marge. Das ergibt 996 € Rohertrag.
    Abzüglich der 92 € laufenden Kosten bleiben rund <strong>904 € pro Monat</strong> übrig.
  </p>
  <p i18n="@@rechnerPage.example.p2">
    Bei 6.800 € Anschaffung heißt das: Nach <strong>7,5 Monaten</strong> hat sich der Automat bezahlt. Alles danach
    ist Ertrag – Monat für Monat, ohne dass jemand hinter der Theke stehen muss. Wer zugekaufte Ware mit 30 % Marge
    verkauft, braucht länger: rund 13 Monate.
  </p>

  <h2 id="mehrere" i18n="@@rechnerPage.second.h2">Was bringt ein zweiter Automat am selben Standort?</h2>
  <p i18n="@@rechnerPage.second.p1">
    Mehr, als man denkt – denn der zweite rechnet sich <em>besser</em> als der erste. Der Grund ist unspektakulär,
    aber er zählt: Versicherung und Mobilfunkanbindung fallen je Standort an, nicht je Automat. Ein zweiter Automat
    verdoppelt Umsatz, Wareneinsatz, Strom und Zahlungsgebühren – die 16,67 € Versicherung und die 20 € Mobilfunk
    zahlen Sie weiterhin nur einmal.
  </p>

  <table class="mb-table">
    <thead>
      <tr><th></th><th i18n="@@rechnerPage.second.th1">Überschuss pro Monat</th><th i18n="@@rechnerPage.second.th2">Amortisation</th></tr>
    </thead>
    <tbody>
      <tr><td i18n="@@rechnerPage.second.row.first">Erster Automat</td><td>904 €</td><td i18n="@@rechnerPage.second.row.first.months">7,5 Monate</td></tr>
      <tr><td i18n="@@rechnerPage.second.row.next">Jeder weitere Automat</td><td>941 €</td><td i18n="@@rechnerPage.second.row.next.months">7,2 Monate</td></tr>
    </tbody>
    <tfoot>
      <tr><td i18n="@@rechnerPage.second.row.two">Zwei Automaten am selben Standort</td><td>1.844 €</td><td i18n="@@rechnerPage.second.row.two.months">7,4 Monate</td></tr>
    </tfoot>
  </table>

  <p i18n="@@rechnerPage.second.p2">
    Der Effekt ist klein, aber er zeigt die Richtung: Ein Automat mehr am selben Standort bringt im Schnitt denselben
    Umsatz noch einmal – bei niedrigeren Zusatzkosten. Deshalb rüstet rund jeder fünfte Kunde nach. Die Voraussetzung
    bleibt dieselbe wie beim ersten: Der Standort muss die Nachfrage hergeben.
  </p>

  <h2 i18n="@@rechnerPage.afa.h2">Ab wann ist eine Amortisation eigentlich gut?</h2>
  <p i18n="@@rechnerPage.afa.p1">
    „7,5 Monate" sagt nur etwas, wenn man weiß, woran man das misst. Es gibt dafür einen Maßstab, den sich niemand
    ausgedacht hat, um etwas zu verkaufen: <strong>Der Fiskus schreibt Verkaufsautomaten über 60 Monate ab.</strong>
    Fünf Jahre – so lange geht der Gesetzgeber davon aus, dass das Gerät im Betrieb ist.
  </p>

  <div class="mb-quote">
    <p i18n="@@rechnerPage.afa.quote">
      <strong>Die Faustregel lautet damit: Alles, was deutlich unter 60 Monaten liegt, ist ein guter Fall.</strong>
      Was in der Abschreibungsdauer bezahlt ist, hat sich gerechnet, bevor es steuerlich überhaupt abgeschrieben war.
    </p>
  </div>

  <p i18n="@@rechnerPage.afa.p2">
    Im Durchschnittsfall sind es 7,5 Monate – rund <strong>52 Monate vor Ende der Abschreibungsdauer</strong>.
    Selbst an einem schwachen Standort mit nur 600 € Umsatz ist eine marktbox bei eigenen Erzeugnissen nach gut
    30 Monaten bezahlt, also immer noch weit innerhalb der Abschreibungsdauer. Eng wird es nur in einer Konstellation:
    schwacher Standort <em>und</em> zugekaufte Ware mit dünner Marge – dann reichen 60 Monate gerade so. Das ist dann
    allerdings kein Automatenproblem, sondern ein Standortproblem.
  </p>

  <h2 i18n="@@rechnerPage.rentbuy.h2">Verkaufsautomat kaufen oder mieten?</h2>
  <p i18n="@@rechnerPage.rentbuy.p1">
    Es gibt dafür eine überraschend einfache Regel, und sie hängt nicht davon ab, wie gut Ihr Automat läuft:
  </p>

  <div class="mb-quote">
    <p i18n="@@rechnerPage.rentbuy.quote">
      <strong>Bis Monat 23 ist Miete günstiger, ab Monat 23 der Kauf.</strong>
      6.800 € geteilt durch 300 € Monatsmiete sind knapp 23 Monate – unabhängig von Ihrem Umsatz,
      weil die Miete in jedem Szenario denselben Betrag kostet.
    </p>
  </div>

  <p i18n="@@rechnerPage.rentbuy.p2">
    Praktisch heißt das: <strong>Mieten Sie, wenn Sie beim Standort noch nicht sicher sind.</strong> Sie riskieren 300 €
    im Monat statt 6.800 € auf einmal, und Ihr Kapital bleibt frei. <strong>Kaufen Sie, wenn der Standort steht</strong>
    und Sie langfristig planen – ab Jahr zwei ist es schlicht die billigere Variante. Beide Optionen finden Sie im
    <a [routerLink]="['/', links.shop]">Shop</a>.
  </p>
  <p i18n="@@rechnerPage.rentbuy.p3">
    Und bevor Sie entscheiden: Für Direktvermarkter und landwirtschaftliche Betriebe gibt es
    <a [routerLink]="['/', links.guide, guideFundingPath]">Fördermittel</a>, die einen erheblichen Teil der Anschaffung abdecken können.
    Das verschiebt die Rechnung deutlich Richtung Kauf.
  </p>

  <h2 i18n="@@rechnerPage.faq.h2">Häufige Fragen</h2>
  <div class="mb-faq">
    @for (faq of faqs; track faq.question) {
      <details>
        <summary>{{ faq.question }}</summary>
        <p>{{ faq.answer }}</p>
      </details>
    }
  </div>

  <div class="mb-final">
    <h2 i18n="@@rechnerPage.final.h2">Ihre Zahlen passen. Und jetzt?</h2>
    <p i18n="@@rechnerPage.final.p">
      Wenn die Rechnung aufgeht, ist der nächste Schritt kein Vertrag, sondern ein Gespräch über Ihren Standort.
      15 Minuten reichen meistens.
    </p>
    <a class="mb-btn" href="#contact" (click)="scrollToAnchor($event, 'contact')" i18n="@@rechnerPage.final.cta1">Standort besprechen</a>
    <a class="mb-btn mb-btn-2" [routerLink]="['/', links.shop]" i18n="@@rechnerPage.final.cta2">Zum Shop</a>
  </div>

  <p class="mb-foot" i18n="@@rechnerPage.foot">
    Kalkulationsgrundlage: Stromverbrauch 60 W im Dauerbetrieb bei 0,45 €/kWh · Zahlungsgebühr 10 ct pro Transaktion,
    keine prozentualen Anteile · Versicherung 200 € pro Jahr und Mobilfunk 20 € netto monatlich (beides je Standort) ·
    Kaufpreis 6.800 € netto, Miete 300 € netto monatlich bei 12 Monaten Laufzeit · 39 Fächer je Automat.
    Umsatz- und Margenwerte sind Mittelwerte aus den ersten 450 marktbox-Automaten an Standorten in Deutschland,
    alle Beträge in Euro. Wartung und Reinigung fallen anlassbezogen an und sind nicht als Fixkosten enthalten.
    Alle Ergebnisse sind Prognosen und keine Zusicherung.
  </p>
</main>
